import React from "react";
import {
  Plus,
  Share2,
  Download,
  FileText,
  Calendar as CalendarIcon,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Users,
  ListTodo,
  ArrowDown,
} from "lucide-react";

const CalendarHeader = ({
  t,
  styles,
  screenSize,
  language,
  calendarStats = {},
  onNewEvent,
  onDownloadImage,
  onDownloadPdf,
  onShare,
  onAssignTask,
  canAddEvent = true,
  canExportCalendar = true,
  canShareCalendar = true,
}) => {
  const isRTL = language === "ar";

  const todayLabel = new Date().toLocaleDateString(isRTL ? "ar-EG" : "en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // The five numbers that used to occupy a full band of stat cards.
  const chips = [
    { key: "total", icon: CalendarIcon, color: "#2e7cba", label: t.totalEvents, value: calendarStats.totalEvents },
    { key: "month", icon: TrendingUp, color: "#2e7cba", label: t.thisMonth, value: calendarStats.thisMonth },
    { key: "done", icon: CheckCircle, color: "#84cc16", label: t.completed, value: calendarStats.completed },
    { key: "late", icon: AlertCircle, color: "#ef4444", label: t.overdue, value: calendarStats.overdueEvents },
    { key: "meet", icon: Users, color: "#ec4899", label: t.meetings, value: calendarStats.meetings },
  ];

  return (
    <div style={styles.headerSection}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #2e7cba, #2e7cba, #2e7cba)",
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: screenSize.isMobile ? "column" : "row",
          alignItems: screenSize.isMobile ? "stretch" : "center",
          gap: screenSize.isMobile ? "14px" : "20px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 className="nx-page-title" style={styles.headerTitle}>{t.calendar}</h1>
          <div
            style={{
              fontSize: screenSize.isMobile ? "12.5px" : "13.5px",
              fontWeight: 600,
              color: styles.tokens.textMuted,
              marginTop: "6px",
            }}
          >
            {todayLabel}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: screenSize.isMobile ? "8px" : "10px",
            marginInlineStart: "auto",
            flexShrink: 0,
          }}
        >
          {canExportCalendar && (
            <>
              <button
                type="button"
                className="cal-nav-btn"
                style={styles.iconButton}
                onClick={onDownloadImage}
                title={isRTL ? "تنزيل صورة" : "Download image"}
                aria-label={isRTL ? "تنزيل صورة" : "Download image"}
              >
                <Download size={18} />
              </button>

              <button
                type="button"
                className="cal-nav-btn"
                style={styles.iconButton}
                onClick={onDownloadPdf}
                title={isRTL ? "تنزيل PDF" : "Download PDF"}
                aria-label={isRTL ? "تنزيل PDF" : "Download PDF"}
              >
                <FileText size={18} />
              </button>
            </>
          )}

          {canShareCalendar && (
            <button
              type="button"
              className="cal-nav-btn"
              style={styles.iconButton}
              onClick={onShare}
              title={t.share}
              aria-label={t.share}
            >
              <Share2 size={18} />
            </button>
          )}

          {/* Assign Task lives in the Tasks panel below the calendar, out of
              sight on load — this jumps there so people know it exists. */}
          {onAssignTask && (
            <button
              type="button"
              style={{
                ...styles.actionButton("default", "medium"),
                flex: screenSize.isMobile ? 1 : "none",
              }}
              onClick={onAssignTask}
              title={isRTL ? "انتقل إلى إسناد المهام بالأسفل" : "Jump to task assignment below"}
            >
              <ListTodo size={18} />
              {isRTL ? "إسناد مهمة" : "Assign Task"}
              <ArrowDown size={16} style={{ opacity: 0.7 }} />
            </button>
          )}

          {canAddEvent && (
            <button
              style={{
                ...styles.actionButton("primary", "medium"),
                marginInlineStart: screenSize.isMobile ? 0 : "4px",
                flex: screenSize.isMobile ? 1 : "none",
              }}
              onClick={onNewEvent}
            >
              <Plus size={18} />
              {screenSize.isMobile ? t.new : t.newEvent}
            </button>
          )}
        </div>
      </div>

      <div style={styles.statStrip}>
        {chips.map(({ key, icon: Icon, color, label, value }) => (
          <span key={key} style={styles.statChip(color)}>
            <Icon size={14} style={{ color, flexShrink: 0 }} />
            {label}
            <b style={styles.statChipValue}>{value ?? 0}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

export default CalendarHeader;
